import React, { useState } from 'react';
import api, { setToken, setRefreshToken } from '../services/api';

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [cfmsId, setCfmsId] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'cfmsId' | 'otp'>('cfmsId');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isNetworkError = (err: any): boolean =>
    /network|fetch|ECONNREFUSED|Failed to fetch/i.test(err?.message || '');

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cfmsId.trim()) {
      setError('Please enter your CFMS ID');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.auth.requestOtp(cfmsId.trim());
      setStep('otp');
    } catch (err: any) {
      if (isNetworkError(err)) {
        // Backend unavailable — proceed with mock OTP flow
        console.log('[AUTH] Backend unavailable — using mock OTP flow');
        setStep('otp');
      } else {
        setError(err.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.auth.verifyOtp(cfmsId.trim(), otp.trim());
      onLogin();
    } catch (err: any) {
      if (isNetworkError(err)) {
        // Backend unavailable — create mock session
        console.log('[AUTH] Backend unavailable — using mock session');
        const mockToken = `mock_session_${cfmsId.trim()}_${Date.now()}`;
        setToken(mockToken);
        setRefreshToken(mockToken);
        onLogin();
      } else {
        setError(err.message || 'Invalid OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('cfmsId');
    setOtp('');
    setError('');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--navy-900) 0%, var(--navy-800) 60%, var(--navy-700) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, var(--gold) 0%, var(--gold-light) 100%)',
              borderRadius: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: 800,
              color: 'var(--navy-900)',
              marginBottom: '16px',
            }}
          >
            +
          </div>
          <h1
            style={{
              fontSize: '22px',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.3px',
              marginBottom: '4px',
            }}
          >
            AIS Officers Healthcare
          </h1>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
            Admin Panel — APCFSS
          </p>
        </div>

        {/* Card */}
        <div
          className="modal"
          style={{ width: '100%', maxHeight: 'none', overflow: 'visible' }}
        >
          <div className="modal-header">
            <h3>{step === 'cfmsId' ? 'Sign In' : 'Verify OTP'}</h3>
            {step === 'otp' && (
              <button className="modal-close" onClick={handleBack} title="Go back">
                &#8592;
              </button>
            )}
          </div>

          <div className="modal-body">
            {step === 'otp' && (
              <p
                style={{
                  fontSize: '13px',
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  marginBottom: '20px',
                }}
              >
                An OTP has been sent for CFMS ID{' '}
                <strong style={{ color: 'var(--text-primary)' }}>{cfmsId}</strong>.
                Check backend console for the OTP.
              </p>
            )}

            {error && (
              <div
                style={{
                  background: 'var(--error-soft)',
                  color: 'var(--error)',
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '16px',
                }}
              >
                {error}
              </div>
            )}

            {step === 'cfmsId' ? (
              <form onSubmit={handleRequestOtp}>
                <div className="form-group">
                  <label className="form-label">CFMS ID</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="e.g. ADMIN001"
                    value={cfmsId}
                    onChange={(e) => setCfmsId(e.target.value)}
                    autoFocus
                    autoCapitalize="characters"
                  />
                </div>
                <button
                  className="header-btn primary"
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    padding: '12px 16px',
                    fontSize: '14px',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? 'Sending OTP...' : 'Request OTP'}
                </button>

                {/* Sample credentials hint */}
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: 'var(--bg-subtle)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: '6px', color: 'var(--text-secondary)' }}>
                    Sample Credentials
                  </div>
                  <div style={{ display: 'grid', gap: '4px' }}>
                    <div><strong>ADMIN001</strong> — Admin (System Administrator)</div>
                    <div><strong>CFMS2024001</strong> — IAS Officer (Rajiv Kumar Sharma)</div>
                    <div><strong>CFMS2024005</strong> — Minister (B. Jaya Prakash)</div>
                    <div><strong>CFMS2024004</strong> — Judge (T. Narasimha Rao)</div>
                  </div>
                  <div style={{ marginTop: '6px', fontStyle: 'italic', fontSize: '11px' }}>
                    OTP appears in the backend console (npm run start:dev)
                  </div>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp}>
                <div className="form-group">
                  <label className="form-label">One-Time Password</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                    autoFocus
                    maxLength={6}
                    style={{
                      letterSpacing: '8px',
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: 700,
                    }}
                  />
                </div>
                <button
                  className="header-btn primary"
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    padding: '12px 16px',
                    fontSize: '14px',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p
          style={{
            textAlign: 'center',
            fontSize: '11px',
            color: 'rgba(255,255,255,0.25)',
            fontWeight: 500,
            marginTop: '24px',
          }}
        >
          Dr. NTR Vaidya Seva Trust — Government of Andhra Pradesh
        </p>
      </div>
    </div>
  );
};

export default Login;
